import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { CategoriesListItem, CategoriesListItemOverride, CategoryType } from "../../../../graphql/types"
import { omit } from "ramda"
import { MultiSelectItem } from "../../../uiKit/MultiSelect/MultiSelect"

export const APPLIED_TO_CUSTOM_ITEM_ID = "applied-to-custom-item-id"

export const getItemsAndCategoriesMultiSelectData = (
    data: Maybe<CategoriesListItem[]>,
    addedToItemIds: string[], // For filtering out already added items
    rootCategoryId: Maybe<string>,
    locationId?: string,
    overrides?: CategoriesListItemOverride[]
): MultiSelectItem[] => {
    if (!data || data.length === 0) {
        return []
    }

    const childrenByParentId = getChildrenByParentId(data)
    const addedToItemIdsSet = new Set(addedToItemIds)

    return getMultiSelectDataForParent(childrenByParentId, rootCategoryId, addedToItemIdsSet, locationId, overrides)
}

export const getMultiSelectDataForParent = (
    childrenByParentId: Map<Maybe<string>, CategoriesListItem[]>,
    parentId: Maybe<string>,
    addedToItemIdsSet: Set<string>,
    locationId?: string, // Undefined for master-list
    overrides?: CategoriesListItemOverride[]
): MultiSelectItem[] => {
    return (
        (childrenByParentId.get(parentId) ?? [])
            .filter(i => {
                return (
                    // Include categories always, but items only if they are not already added
                    (i.type === CategoryType.Category || !addedToItemIdsSet.has(i.id)) &&
                    // Exclude card program items
                    !i.cardProgramId
                )
            })
            .map(i => {
                // Filter out categories that have no children after filtering
                const filteredChildren =
                    i.type === CategoryType.Category
                        ? getMultiSelectDataForParent(
                              childrenByParentId,
                              i.id,
                              addedToItemIdsSet,
                              locationId,
                              overrides
                          )
                        : undefined
                if (i.type === CategoryType.Category && (!filteredChildren || filteredChildren.length === 0)) {
                    return null
                }

                const isInactive = resolveInactiveForItemsAndCategoriesMultiSelectItem(i, overrides ?? [], locationId)

                return {
                    id: i.id,
                    label: i.name,
                    renderCustomLabel: () =>
                        isInactive ? <span className="text-black/50">{i.name} (Inactive)</span> : i.name,
                    isInactive: isInactive,
                    children: filteredChildren,
                }
            })
            .filter(Boolean)
            // @ts-expect-error
            .sort((a: MultiSelectItem, b: MultiSelectItem) => {
                if (a.isInactive === b.isInactive) {
                    return 0
                }
                return a.isInactive ? 1 : -1
            }) as MultiSelectItem[]
    )
}

const resolveInactiveForItemsAndCategoriesMultiSelectItem = (
    item: CategoriesListItem,
    overrides: CategoriesListItemOverride[],
    locationId: Maybe<string>
): boolean => {
    const override = overrides?.find(o => o.locationId === locationId && o.data.id === item.id)
    if (override && override.data.isActive != null) {
        return override.data.isActive === false
    }
    return item.isActive === false
}

const getChildrenByParentId = (items: CategoriesListItem[]): Map<Maybe<string>, CategoriesListItem[]> =>
    items.reduce((acc, item) => {
        const children = acc.get(item.parentId) ?? []
        children.push(item)
        acc.set(item.parentId, children)
        return acc
    }, new Map<Maybe<string>, CategoriesListItem[]>())

export const getItemsAndCategoriesMap = (items: Maybe<CategoriesListItem[]>): Record<string, CategoriesListItem> => {
    const map: Record<string, CategoriesListItem> = {}

    if (!items) {
        return {}
    }

    items.forEach(i => {
        map[i.id] = omit(["children"], i)
    })
    return map
}

export const getSelectedItemsWithoutCustom = (
    selectedIds: string[]
): {
    selectedItemsIds: string[]
    hasCustomItem: boolean
} => {
    let hasCustomItem = false

    const result = selectedIds.filter(id => {
        if (id === APPLIED_TO_CUSTOM_ITEM_ID) {
            hasCustomItem = true
            return false
        }
        return true
    })

    return {
        selectedItemsIds: result,
        hasCustomItem: hasCustomItem,
    }
}
